import React, { useState } from 'react'

import {
  Button,
  Card,
  Container,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@mui/material'

import { Page } from 'src/components/base'
import useAuth from 'src/hooks/useAuth'
import useRole from 'src/hooks/useRole'
import useSettings from 'src/hooks/useSettings'
import {
  useGetAllSessions,
  useGetSessions
} from 'src/services/Query/auth/auth.query'

const formatDateTime = (value: string | null) => {
  if (!value) {
    return '-'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value
  }

  return date.toLocaleString()
}

const TAB_MY = 0
const TAB_ALL = 1

const Sessions: React.FC = () => {
  const { themeStretch } = useSettings()
  const { logoutFromAllDevices } = useAuth()
  const { isAdmin } = useRole()

  const [activeTab, setActiveTab] = useState(TAB_MY)

  const {
    data: mySessions,
    isLoading: isMyLoading,
    isError: isMyError
  } = useGetSessions({
    options: { refetchOnWindowFocus: false }
  })

  const {
    data: allSessions,
    isLoading: isAllLoading,
    isError: isAllError
  } = useGetAllSessions({
    options: {
      refetchOnWindowFocus: false,
      enabled: isAdmin && activeTab === TAB_ALL
    }
  })

  const showAllTab = isAdmin && activeTab === TAB_ALL
  const sessions = showAllTab ? allSessions : mySessions
  const isLoading = showAllTab ? isAllLoading : isMyLoading
  const isError = showAllTab ? isAllError : isMyError
  const hasSessions = !!sessions && sessions.length > 0
  const colSpan = showAllTab ? 9 : 8

  const handleRevokeAll = async () => {
    try {
      await logoutFromAllDevices()
    } catch (error) {
      console.error('Failed to revoke all sessions', error)
    }
  }

  return (
    <Page title="Sessions">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4">
            {isAdmin ? 'Sessions' : 'Active Sessions'}
          </Typography>
        </Stack>

        {isAdmin && (
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab label="Active Sessions" value={TAB_MY} />
            <Tab label="All Sessions" value={TAB_ALL} />
          </Tabs>
        )}

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {showAllTab && <TableCell>User</TableCell>}
                  <TableCell>Device ID</TableCell>
                  <TableCell>Browser</TableCell>
                  <TableCell>OS</TableCell>
                  <TableCell>IP address</TableCell>
                  <TableCell>Device info</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell>Created at</TableCell>
                  <TableCell>Expires at</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={colSpan}>Loading sessions...</TableCell>
                  </TableRow>
                )}

                {isError && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      Failed to load sessions. Please try again.
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && !hasSessions && (
                  <TableRow>
                    <TableCell colSpan={colSpan}>No active sessions.</TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  sessions?.map((session) => (
                    <TableRow key={session.id}>
                      {showAllTab && (
                        <TableCell>{session.userName || '-'}</TableCell>
                      )}
                      <TableCell>{session.deviceId ?? '-'}</TableCell>
                      <TableCell>{session.browser || '-'}</TableCell>
                      <TableCell>{session.os || '-'}</TableCell>
                      <TableCell>{session.ipAddress || '-'}</TableCell>
                      <TableCell>{session.deviceInfo || '-'}</TableCell>
                      <TableCell>
                        {formatDateTime(session.lastUsedAt)}
                      </TableCell>
                      <TableCell>{formatDateTime(session.createdAt)}</TableCell>
                      <TableCell>{formatDateTime(session.expiresAt)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Page>
  )
}

export default Sessions
